import {
  ActionBarPrimitive,
  AuiIf,
  ErrorPrimitive,
  MessagePrimitive,
  useAuiState,
} from '@assistant-ui/react';
import {
  Button,
  DotProgress,
  Icon,
  IconButton,
  TextField,
  ToggleButton,
} from '@wandb/components';
import copyToClipboard from 'copy-to-clipboard';
import React, {type FC, useCallback, useEffect, useRef, useState} from 'react';

import {extractTurnId} from '../../agentChat/runtime/turn';
import {useSubmitAgentFeedback} from '../../agentChat/useSubmitAgentFeedback';
import {useShareTracesPolicy} from '../AgentChat/useAgentChatAvailability';
import {useNightModeRevertFilter} from './useNightModeRevertFilter';
import {useTrackWBAgentInspection} from './useTrackWBAgent';
import {MarkdownText} from './WBAgentMarkdown';
import {WBAgentReasoning} from './WBAgentReasoning';
import {WBAgentToolCall} from './WBAgentToolCall';

const messagePartComponents: NonNullable<
  React.ComponentProps<typeof MessagePrimitive.Parts>['components']
> = {
  Reasoning: WBAgentReasoning,
  Text: MarkdownText,
  tools: {
    Fallback: WBAgentToolCall,
  },
};

export const AssistantMessage: FC = () => {
  const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isFading, setIsFading] = useState<boolean>(false);
  const [sentiment, setSentiment] = useState<'positive' | 'negative' | null>(
    null
  );
  const {defaultSharingEnabled, lockedOff, loading} = useShareTracesPolicy();
  const allowPerTurnShareOptIn =
    !loading && !defaultSharingEnabled && !lockedOff;
  const showFeedbackBar = !loading && !lockedOff;

  useEffect(() => {
    if (!isSubmitted) {
      return;
    }
    const fadeTimer = setTimeout(() => setIsFading(true), 1000);
    const removeTimer = setTimeout(() => {
      setIsSubmitted(false);
      setIsFading(false);
    }, 1500);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, [isSubmitted]);

  const submitFeedback = useSubmitAgentFeedback();
  const messageId = useAuiState(s => s.message.id);
  const turnId = extractTurnId(messageId);
  const {trackFeedbackSubmitted} = useTrackWBAgentInspection();

  const handleDismissFeedbackForm = (feedbackReasoning?: string) => {
    setIsFeedbackFormOpen(false);

    if (turnId && sentiment !== null) {
      submitFeedback(
        turnId,
        sentiment === 'positive',
        feedbackReasoning,
        allowPerTurnShareOptIn
      );
      trackFeedbackSubmitted(
        sentiment === 'positive',
        Boolean(feedbackReasoning && feedbackReasoning.trim().length > 0)
      );
    }
  };

  const handleSentimentChange = (
    newSentiment: 'positive' | 'negative' | null
  ) => {
    setSentiment(newSentiment);
    setIsFeedbackFormOpen(newSentiment !== null);

    // Retract any prior feedback so the backend stays in sync with the
    // user's current intent. Handles both toggling the active thumb off
    // (newSentiment === null) and switching between thumbs.
    if (sentiment !== null && turnId) {
      submitFeedback(turnId, null);
      trackFeedbackSubmitted(null, false);
    }
  };

  return (
    <MessagePrimitive.Root
      className="mx-auto w-full max-w-(--thread-max-width) pt-16"
      data-role="assistant">
      <div className="flex flex-col break-words px-2 text-moon-650 text-[15px] leading-[21px]">
        <MessagePrimitive.Parts components={messagePartComponents} />
        <AuiIf
          condition={s =>
            s.thread.isRunning &&
            s.message.isLast &&
            !s.message.parts.some(
              p =>
                (p.type === 'tool-call' || p.type === 'reasoning') &&
                p.status?.type === 'running'
            )
          }>
          <div className="mt-12 mb-8 flex flex-col items-start self-stretch text-[#C5C7CC]">
            <DotProgress aria-label="agent thinking" />
          </div>
        </AuiIf>
      </div>
      <MessagePrimitive.Error>
        <ErrorPrimitive.Root className="mt-8 flex items-center gap-8 rounded-lg border border-red-300 bg-red-50 px-12 py-8 text-[15px] text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          <span className="inline-flex shrink-0">
            <Icon icon="warning" color="red" />
          </span>
          <ErrorPrimitive.Message className="flex-1" />
          <CopyErrorButton />
        </ErrorPrimitive.Root>
      </MessagePrimitive.Error>
      <AuiIf condition={s => !(s.thread.isRunning && s.message.isLast)}>
        <div className="flex justify-between items-center pb-16">
          <CopyMessageButton />
          {showFeedbackBar && (
            <FeedbackBar
              allowPerTurnShareOptIn={allowPerTurnShareOptIn}
              sentiment={sentiment}
              onSentimentChange={handleSentimentChange}
            />
          )}
        </div>
      </AuiIf>
      {isFeedbackFormOpen && (
        <FeedbackReasoningForm
          onDismiss={handleDismissFeedbackForm}
          onSubmit={setIsSubmitted}
          sentiment={sentiment}
        />
      )}
      {isSubmitted && (
        <div
          className={`flex items-center gap-7 text-moon-800 font-normal transition-opacity duration-500 ${
            isFading ? 'opacity-0' : 'opacity-100'
          }`}>
          <Icon icon="checkmark" color="teal" /> Thanks for the feedback!
        </div>
      )}
    </MessagePrimitive.Root>
  );
};

/**
 * CopyMessageButton is a copy button that hides entirely when there is no text
 * content to copy. AUI's `ActionBarPrimitive.Copy` only *disables* the button;
 * this component will be hidden entirely.
 */
const CopyMessageButton: FC = () => {
  const hasText = useAuiState(s =>
    s.message.parts.some(
      (c: {type: string; text?: string}) =>
        c.type === 'text' && (c.text?.length ?? 0) > 0
    )
  );
  const {trackCopied} = useTrackWBAgentInspection();

  if (!hasText) {
    return null;
  }

  return (
    <ActionBarPrimitive.Copy asChild>
      <IconButton
        variant="ghost"
        icon="copy"
        aria-label="copy"
        onPress={() => trackCopied('message')}
      />
    </ActionBarPrimitive.Copy>
  );
};

/**
 * CopyErrorButton is a copy button placed within error messages, allowing the
 * user to copy *just* the error message, as opposed to message-level copy
 * buttons which may copy partial text responses above an error.
 */
const CopyErrorButton: FC = () => {
  const error = useAuiState(s =>
    s.message.status?.type === 'incomplete' &&
    s.message.status.reason === 'error'
      ? s.message.status.error
      : undefined
  );
  const {trackCopied} = useTrackWBAgentInspection();

  const handleCopy = useCallback(() => {
    if (error != null) {
      copyToClipboard(String(error));
      trackCopied('error');
    }
  }, [error, trackCopied]);

  if (error == null) {
    return null;
  }

  return (
    <IconButton
      variant="ghost"
      size="sm"
      icon="copy"
      aria-label="copy error"
      onClick={handleCopy}
    />
  );
};

const shareDisclaimerTooltip = (label: string) => ({
  children: (
    <div style={{textAlign: 'center'}}>
      <div style={{fontWeight: 600}}>{label}</div>
      <div>Shares chat with W&B</div>
    </div>
  ),
});

const FeedbackBar = ({
  allowPerTurnShareOptIn,
  sentiment,
  onSentimentChange,
}: {
  allowPerTurnShareOptIn: boolean;
  sentiment: 'positive' | 'negative' | null;
  onSentimentChange: (s: 'positive' | 'negative' | null) => void;
}) => {
  const handleThumb = (value: 'positive' | 'negative') => {
    onSentimentChange(sentiment === value ? null : value);
  };

  return (
    <div className="ml-auto">
      <IconButton
        aria-label="positive feedback"
        icon="thumbs-up"
        tooltip={
          allowPerTurnShareOptIn
            ? shareDisclaimerTooltip('Good response')
            : undefined
        }
        onPress={() => handleThumb('positive')}
        variant={sentiment === 'positive' ? 'primary' : 'ghost'}
      />
      <IconButton
        aria-label="negative feedback"
        icon="thumbs-down"
        tooltip={
          allowPerTurnShareOptIn
            ? shareDisclaimerTooltip('Bad response')
            : undefined
        }
        onPress={() => handleThumb('negative')}
        variant={sentiment === 'negative' ? 'primary' : 'ghost'}
      />
    </div>
  );
};

const NEGATIVE_SUGGESTIONS = ['Inaccurate', 'Not relevant', 'Too vague'];
const FeedbackReasoningForm = ({
  onDismiss,
  onSubmit,
  sentiment,
}: {
  onDismiss: (feedbackReasoning?: string) => void;
  onSubmit: (isSubmitted: boolean) => void;
  sentiment: 'positive' | 'negative' | null;
}) => {
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(
    new Set()
  );
  const nightModeRevertFilter = useNightModeRevertFilter();
  const [details, setDetails] = useState('');
  const isNegative = sentiment === 'negative';

  const handleToggleSuggestion = (suggestion: string, isSelected: boolean) => {
    setSelectedSuggestions(prev => {
      const next = new Set(prev);
      if (isSelected) {
        next.add(suggestion);
      } else {
        next.delete(suggestion);
      }
      return next;
    });
  };

  // Selected suggestions are always negative,
  // reset them when sentiment changes.
  useEffect(() => {
    if (sentiment !== 'negative') {
      setSelectedSuggestions(new Set());
    }
  }, [sentiment]);

  const buildReasoning = (): string | undefined => {
    const parts: string[] = [];
    if (sentiment === 'negative') {
      parts.push(...selectedSuggestions);
    }
    if (details.trim()) {
      parts.push(details.trim());
    }
    return parts.length > 0 ? parts.join(', ') : undefined;
  };

  const handleSubmit = () => {
    onDismiss(buildReasoning());
    onSubmit(true);
  };

  // Use isThreadRunning going from `false` to `true` as signal
  // that the user submitted a new prompt
  const isThreadRunning = useAuiState(s => s.thread.isRunning);
  const wasRunningRef = useRef(isThreadRunning);
  const dismissSilentlyRef = useRef<() => void>(() => {});
  dismissSilentlyRef.current = () => {
    onDismiss(buildReasoning());
  };
  // If the user sends a new prompt while the form is open, submit any
  // existing reasoning and dismiss the form
  useEffect(() => {
    if (!wasRunningRef.current && isThreadRunning) {
      dismissSilentlyRef.current();
    }
    wasRunningRef.current = isThreadRunning;
  }, [isThreadRunning]);

  return (
    <div
      className="relative flex flex-col gap-12 p-12 rounded-xl"
      role="region"
      aria-label="Message feedback">
      <div
        className="absolute inset-0 rounded-xl border border-moon-250 bg-white dark:bg-moon-850 dark:border-moon-650"
        style={{filter: nightModeRevertFilter}}
      />
      <div className="relative flex items-start self-stretch justify-between">
        <span className="text-lg font-semibold text-moon-800">
          {isNegative ? 'What went wrong?' : 'What went well?'}
        </span>
        <IconButton
          icon="close"
          size="sm"
          aria-label="close feedback box"
          variant="ghost"
          onClick={() => onDismiss(buildReasoning())}
        />
      </div>
      {isNegative && (
        <div className="relative flex items-start content-start flex-wrap gap-8">
          {NEGATIVE_SUGGESTIONS.map(suggestion => (
            <ToggleButton
              key={suggestion}
              variant="secondary"
              isSelected={selectedSuggestions.has(suggestion)}
              onChange={isSelected =>
                handleToggleSuggestion(suggestion, isSelected)
              }>
              {suggestion}
            </ToggleButton>
          ))}
        </div>
      )}
      <div className="relative">
        <TextField
          aria-label="feedback details"
          fullWidth={true}
          placeholder="Share details (optional)"
          value={details}
          onChange={setDetails}
        />
      </div>
      <div className="relative flex justify-end">
        <Button
          variant="primary"
          onPress={handleSubmit}
          isDisabled={details.trim() === '' && selectedSuggestions.size === 0}>
          Submit
        </Button>
      </div>
    </div>
  );
};
